// src/modules/units/tyres/shared/tyre-vehicle-filter.js
//
// The vehicle filter vocabulary — "which tyres belong to this vehicle?" — in one
// place, pure (no DOM, no fetch), so the report and the register cannot drift.
//
// Four kinds, because the fleet has three different notions of "a vehicle":
//
//  - UNIT     the gateway (a row of GET /customer/{id}/unit), keyed by its IMEI.
//             Matches a tyre's `imeiNumber` — the tyres reporting through that
//             gateway. This is the register's original Unit filter.
//  - TRUCK    the truck body only (GET /customer/{id}/truck), keyed by its `Id`.
//             Matches a tyre's `unitId`: a sensor-fitted tyre carries the Id of the
//             truck or trailer the sensor belongs to.
//  - TRAILER  one trailer (GET /customer/{id}/trailer), keyed by its `Id`, matched
//             on `unitId` the same way.
//  - TRUCK_WITH_TRAILERS  a truck plus every trailer CURRENTLY hitched to it — a
//             trailer is hitched when its `truckIMEI` equals the truck's `IMEI`. The
//             hitch is read from the trailer list at filter time, so re-hitching a
//             trailer moves its tyres with it; nothing is frozen onto the tyre.
//
// A vehicle filter only ever matches FITTED tyres — stock tyres have no unitId or
// imeiNumber — which is what "the tyres on this vehicle" means.

export const VEHICLE_KIND = Object.freeze({
  UNIT: 'unit',
  TRUCK: 'truck',
  TRUCK_WITH_TRAILERS: 'truckWithTrailers',
  TRAILER: 'trailer'
});

// Dropdown order and wording. `placeholder` is the vehicle select's empty
// (no-filter) option, matching the register's "Select a Unit".
export const VEHICLE_KIND_OPTIONS = Object.freeze([
  { value: VEHICLE_KIND.UNIT, label: 'Unit', noun: 'Unit', placeholder: 'Select a Unit' },
  { value: VEHICLE_KIND.TRUCK, label: 'Truck', noun: 'Truck', placeholder: 'Select a Truck' },
  { value: VEHICLE_KIND.TRUCK_WITH_TRAILERS, label: 'Truck + trailers', noun: 'Truck + trailers', placeholder: 'Select a Truck' },
  { value: VEHICLE_KIND.TRAILER, label: 'Trailer', noun: 'Trailer', placeholder: 'Select a Trailer' }
]);

export function vehicleKindMeta(kind) {
  return VEHICLE_KIND_OPTIONS.find((o) => o.value === kind) || VEHICLE_KIND_OPTIONS[0];
}

const isBlank = (v) => v === null || v === undefined || v === '';
const byLabel = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true });

// The Unit filter's options, derived from a units list. Each vehicle is offered by
// its gateway IMEI — the key GET /api/tyre actually filters on (`imeiNumber`; there
// is no `unitId` parameter) — and labelled by unitName, falling back to the IMEI
// when a name is absent. Vehicles WITHOUT an IMEI are left out on purpose: with no
// imeiNumber there is nothing to filter tyres by, so the option could only ever
// return an empty list. De-duped by IMEI and sorted by label.
export function unitOptionsFromUnits(units = []) {
  const seen = new Map();
  for (const u of units) {
    const imei = u?.imeiNumber;
    if (isBlank(imei)) continue;
    const key = String(imei);
    if (!seen.has(key)) seen.set(key, u?.unitName ? String(u.unitName) : key);
  }
  return [...seen.entries()].map(([id, label]) => ({ id, label })).sort(byLabel);
}

// Trailers hitched to a truck right now (truckIMEI === truck.IMEI).
export function hitchedTrailers(truck, trailers = []) {
  const imei = truck?.IMEI;
  if (isBlank(imei)) return [];
  return trailers.filter((tr) => !isBlank(tr?.truckIMEI) && String(tr.truckIMEI) === String(imei));
}

// Id-keyed options for a truck or trailer list, de-duped, sorted by label.
function idOptions(rows, labelOf) {
  const seen = new Map();
  for (const r of rows) {
    if (isBlank(r?.Id)) continue;
    const key = String(r.Id);
    if (!seen.has(key)) seen.set(key, labelOf(r));
  }
  return [...seen.entries()].map(([id, label]) => ({ id, label })).sort(byLabel);
}

/**
 * The vehicle select's options for a kind.
 * @param {string} kind  a VEHICLE_KIND
 * @param {{units?:object[], trucks?:object[], trailers?:object[]}} lists
 * @returns {{id:string,label:string}[]}
 */
export function vehicleOptions(kind, { units = [], trucks = [], trailers = [] } = {}) {
  const truckName = (t) => String(t?.name || t?.IMEI || t?.Id);
  switch (kind) {
    case VEHICLE_KIND.UNIT:
      return unitOptionsFromUnits(units);
    case VEHICLE_KIND.TRUCK:
      return idOptions(trucks, truckName);
    case VEHICLE_KIND.TRUCK_WITH_TRAILERS:
      // The label says what the pick will include, so "no trailers" is visible
      // before choosing rather than discovered from the numbers.
      return idOptions(trucks, (t) => {
        const n = hitchedTrailers(t, trailers).length;
        return `${truckName(t)} (+${n} trailer${n === 1 ? '' : 's'})`;
      });
    case VEHICLE_KIND.TRAILER: {
      const trucksByImei = new Map(trucks.filter((t) => !isBlank(t?.IMEI)).map((t) => [String(t.IMEI), t]));
      return idOptions(trailers, (tr) => {
        const name = String(tr?.name || tr?.Id);
        const truck = isBlank(tr?.truckIMEI) ? null : trucksByImei.get(String(tr.truckIMEI));
        return truck ? `${name} · on ${truckName(truck)}` : name;
      });
    }
    default:
      return [];
  }
}

/**
 * A tyre predicate for a vehicle pick, or null when there is nothing to filter by
 * (blank id). Truck + trailers resolves the hitched trailers once, here.
 * @returns {((tyre:object)=>boolean)|null}
 */
export function vehicleMatcher(kind, id, { trucks = [], trailers = [] } = {}) {
  if (isBlank(id)) return null;
  const want = String(id);
  switch (kind) {
    case VEHICLE_KIND.UNIT:
      return (t) => String(t?.imeiNumber ?? '') === want;
    case VEHICLE_KIND.TRUCK:
    case VEHICLE_KIND.TRAILER:
      return (t) => String(t?.unitId ?? '') === want;
    case VEHICLE_KIND.TRUCK_WITH_TRAILERS: {
      const truck = trucks.find((t) => String(t?.Id) === want);
      const ids = new Set([want, ...hitchedTrailers(truck, trailers).map((tr) => String(tr.Id))]);
      return (t) => ids.has(String(t?.unitId ?? ''));
    }
    default:
      return null;
  }
}

// Which lists a kind needs: its options and its matcher. Unit needs only units;
// trailer labels name their truck, and truck + trailers needs both.
export function listsForKind(kind) {
  switch (kind) {
    case VEHICLE_KIND.UNIT: return ['units'];
    case VEHICLE_KIND.TRUCK: return ['trucks'];
    default: return ['trucks', 'trailers'];
  }
}
